<script setup lang="ts">
import { computed } from 'vue'
import { ElProgress } from 'element-plus'

/**
 * `ElProgress` with a fully rounded bar that cannot outgrow its parent, drawn in the
 * theme's own colour. Everything else — `percentage`, `stroke-width`, `type`, `format` —
 * falls through untyped, so Element Plus's own documentation is the reference.
 */
type ProgressColor = string | { color: string; percentage: number }[]

const props = defineProps<{
  color?: ProgressColor
  /** Paints the bar by meaning (`success`, `warning`, `exception`) instead. */
  status?: 'success' | 'warning' | 'exception'
}>()

/**
 * Without this the bar is element-plus's hardcoded `#20a0ff` (`STATUS_COLOR_MAP.default`),
 * which is blue in an app whose primary is not — the one property the component does not
 * take from the theme.
 */
const color = computed(
  () => props.color ?? (props.status ? undefined : 'var(--el-color-primary)')
)
</script>

<template>
  <ElProgress
    class="shared-progress"
    :color="color"
    :status="status"
    v-bind="$attrs"
  />
</template>

<style lang="scss" scoped>
/* Element Plus sizes the bar by its own track width; keep it inside the parent. */
.shared-progress :deep(.el-progress-bar) {
  max-width: 100%;
}

.shared-progress :deep(.el-progress-bar__outer),
.shared-progress :deep(.el-progress-bar__inner) {
  border-radius: 999px;
}
</style>
